import { useAuth } from "@elit-hair/elit-tools";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Button,
    EmptyState,
    ErrorState,
    Icon,
    LoadingState,
    PaginationControls,
    Spinner,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbDownload, TbFileImport, TbPlus, TbRefresh, TbUpload } from "react-icons/tb";
import { toast } from "sonner";

import { SearchBar } from "@/components/ui/search-bar";
import { PERMISSIONS } from "@/constants/permissions";
import { ImportStringsDialog } from "@/pages/strings/components/import-strings-dialog";
import { PublishReleaseDialog } from "@/pages/strings/components/publish-release-dialog";
import { StringCard } from "@/pages/strings/components/string-card";
import { StringFormDialog } from "@/pages/strings/components/string-form-dialog";
import { StringsTable } from "@/pages/strings/components/strings-table";
import { UnpublishedBanner } from "@/pages/strings/components/unpublished-banner";
import { UnpublishedChangesDialog } from "@/pages/strings/components/unpublished-changes-dialog";
import { STRINGS_PAGE_SIZE } from "@/pages/strings/constants";
import {
    computeCompletion,
    computeUnpublishedCount,
    downloadJson,
    exportFilename,
    extractApiMessage,
    getUnpublishedStrings,
} from "@/pages/strings/utils/strings.utils";
import { useDeleteString, useExportStrings, useLatestStringsRelease, useStringsList } from "@/queries/strings.queries";
import { StringRecord } from "@/types/strings.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

export const Route = createFileRoute(APP_ROUTES_FULL.STRINGS)({
    component: StringsPage,
    staticData: {
        config: {
            breadcrumb: { title: "strings", to: APP_ROUTES_TO.STRINGS },
            title: "strings",
        },
        permissions: [PERMISSIONS.APP_STRINGS_LIST],
    },
});

type FormDialogState = "create" | StringRecord | null;

function StringsPage() {
    const { t } = useTranslation();
    const { userData } = useAuth();
    const permissionCodes = userData?.permissionCodes || [];

    const canCreate = permissionCodes.includes(PERMISSIONS.APP_STRINGS_CREATE);
    const canUpdate = permissionCodes.includes(PERMISSIONS.APP_STRINGS_UPDATE);
    const canDelete = permissionCodes.includes(PERMISSIONS.APP_STRINGS_DELETE);
    const canExport = permissionCodes.includes(PERMISSIONS.APP_STRINGS_EXPORT);
    const canImport = permissionCodes.includes(PERMISSIONS.APP_STRINGS_IMPORT);
    const canPublish = permissionCodes.includes(PERMISSIONS.APP_STRINGS_PUBLISH);

    const { data: strings = [], isLoading, isFetching, isError, error, refetch } = useStringsList();
    const { data: release, isFetching: isReleaseFetching, refetch: refetchRelease } = useLatestStringsRelease();
    const isRefreshing = isFetching || isReleaseFetching;
    const deleteMutation = useDeleteString();
    const exportMutation = useExportStrings();

    const [search, setSearch] = useState("");
    const [missingOnly, setMissingOnly] = useState(false);
    const [page, setPage] = useState(1);

    const [formDialog, setFormDialog] = useState<FormDialogState>(null);
    const [deleteTarget, setDeleteTarget] = useState<StringRecord | null>(null);
    const [importOpen, setImportOpen] = useState(false);
    const [publishOpen, setPublishOpen] = useState(false);
    const [changesOpen, setChangesOpen] = useState(false);
    const [deletedThisSession, setDeletedThisSession] = useState<StringRecord[]>([]);
    const [importPruned, setImportPruned] = useState(false);

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();

        return strings
            .filter((item) => {
                if (missingOnly && computeCompletion(item.translations).missing.length === 0) {
                    return false;
                }

                if (query === "") return true;

                const haystack = [item.key, item.description ?? "", ...Object.values(item.translations)]
                    .join(" ")
                    .toLowerCase();

                return haystack.includes(query);
            })
            // Most recently created/edited first — the backend gives no
            // ordering guarantee, so without this a just-created string can
            // land anywhere, including pages the user never thinks to check.
            .sort((a, b) => {
                const aTime = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
                const bTime = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
                return bTime - aTime;
            });
    }, [strings, search, missingOnly]);

    useEffect(() => {
        setPage(1);
    }, [search, missingOnly]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / STRINGS_PAGE_SIZE));
    const paginated = useMemo(
        () => filtered.slice((page - 1) * STRINGS_PAGE_SIZE, page * STRINGS_PAGE_SIZE),
        [filtered, page]
    );

    const unpublishedCount = useMemo(() => computeUnpublishedCount(strings, release), [strings, release]);
    const unpublishedChanges = useMemo(() => getUnpublishedStrings(strings, release), [strings, release]);
    const hasUnpublishedChanges =
        !isLoading && (unpublishedCount > 0 || deletedThisSession.length > 0 || importPruned);

    const handleRefresh = () => {
        refetch();
        refetchRelease();
    };

    const handleExport = async () => {
        try {
            const snapshot = await exportMutation.mutateAsync();
            downloadJson(snapshot, exportFilename(snapshot.sourceEnvironment));
            toast.success(t("strings_exported"));
        } catch (err) {
            toast.error(extractApiMessage(err, t("export_failed")));
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteMutation.mutateAsync(deleteTarget.id);
            setDeletedThisSession((prev) => [deleteTarget, ...prev]);
            toast.success(t("string_deleted", { key: deleteTarget.key }));
        } catch (err) {
            toast.error(extractApiMessage(err, t("failed_to_delete_string")));
        } finally {
            setDeleteTarget(null);
        }
    };

    if (isError) {
        return <ErrorState title={t("failed_to_load_strings")} message={error?.message} onRetry={handleRefresh} />;
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">{t("strings")}</h1>
                    <p className="text-muted-foreground">
                        {t("strings_count_across_languages", { count: strings.length })}
                    </p>
                </div>

                <div className="flex flex-wrap gap-x-2 gap-y-3 w-full sm:w-auto">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
                                {isRefreshing ? (
                                    <Spinner size="sm" className="md:mr-2" />
                                ) : (
                                    <Icon icon={TbRefresh} className="md:mr-2" />
                                )}
                                <span className="hidden md:inline">{t("refresh")}</span>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("refresh")}</TooltipContent>
                    </Tooltip>
                    {canImport && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="outline" onClick={() => setImportOpen(true)}>
                                    <Icon icon={TbFileImport} className="md:mr-2" />
                                    <span className="hidden md:inline">{t("import")}</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("import")}</TooltipContent>
                        </Tooltip>
                    )}
                    {canExport && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="outline" onClick={handleExport} disabled={exportMutation.isPending}>
                                    <Icon icon={TbDownload} className="md:mr-2" />
                                    <span className="hidden md:inline">{t("export")}</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("export")}</TooltipContent>
                        </Tooltip>
                    )}
                    {canPublish && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="outline" onClick={() => setPublishOpen(true)}>
                                    <Icon icon={TbUpload} className="md:mr-2" />
                                    <span className="hidden md:inline">{t("publish")}</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("publish")}</TooltipContent>
                        </Tooltip>
                    )}
                    {canCreate && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button onClick={() => setFormDialog("create")}>
                                    <Icon icon={TbPlus} className="md:mr-2" />
                                    <span className="hidden md:inline">{t("create_string")}</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("create_string")}</TooltipContent>
                        </Tooltip>
                    )}
                </div>
            </div>

            {hasUnpublishedChanges && (
                <UnpublishedBanner
                    count={unpublishedCount}
                    version={release?.version ?? null}
                    canPublish={canPublish}
                    onPublishClick={() => setPublishOpen(true)}
                    onViewClick={() => setChangesOpen(true)}
                />
            )}

            <div className="flex flex-wrap items-center gap-3">
                <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder={t("search_strings_placeholder")}
                    className="sm:w-72"
                />
                <Button
                    variant={missingOnly ? "default" : "outline"}
                    size="sm"
                    onClick={() => setMissingOnly((v) => !v)}
                >
                    {t("missing_translations_only")}
                </Button>
            </div>

            {isLoading ? (
                <LoadingState />
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center gap-4">
                    <EmptyState
                        variant="simple"
                        title={strings.length === 0 ? t("no_strings_yet") : t("no_matches")}
                        description={strings.length === 0 ? t("create_your_first_string") : t("try_different_search")}
                    />
                    {strings.length === 0 && canCreate && (
                        <Button onClick={() => setFormDialog("create")}>
                            <Icon icon={TbPlus} className="mr-2" />
                            {t("create_string")}
                        </Button>
                    )}
                </div>
            ) : (
                <>
                    <div className="hidden md:block">
                        <StringsTable
                            strings={paginated}
                            isLoading={false}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                            onEdit={setFormDialog}
                            onDelete={setDeleteTarget}
                        />
                    </div>
                    <div className="md:hidden space-y-3">
                        {paginated.map((item) => (
                            <StringCard
                                key={item.id}
                                record={item}
                                canUpdate={canUpdate}
                                canDelete={canDelete}
                                onEdit={setFormDialog}
                                onDelete={setDeleteTarget}
                            />
                        ))}
                    </div>
                    <PaginationControls currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                </>
            )}

            <StringFormDialog
                open={formDialog !== null}
                record={formDialog === "create" ? null : formDialog}
                allStrings={strings}
                onClose={() => setFormDialog(null)}
                onSuccess={(action) => {
                    setFormDialog(null);
                    setPage(1);
                    toast.success(action === "created" ? t("string_created") : t("string_updated"));
                }}
            />

            <AlertDialog open={deleteTarget !== null} onOpenChange={(next) => !next && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("delete_string")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("delete_string_confirmation", { key: deleteTarget?.key })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeleteTarget(null)}>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t("delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {canImport && (
                <ImportStringsDialog
                    open={importOpen}
                    existing={strings}
                    onClose={() => setImportOpen(false)}
                    onDeleted={() => setImportPruned(true)}
                />
            )}

            {canPublish && (
                <PublishReleaseDialog
                    open={publishOpen}
                    stringsCount={strings.length}
                    onClose={() => setPublishOpen(false)}
                    onPublished={() => {
                        setDeletedThisSession([]);
                        setImportPruned(false);
                    }}
                />
            )}

            <UnpublishedChangesDialog
                open={changesOpen}
                onClose={() => setChangesOpen(false)}
                changes={unpublishedChanges}
                deletedThisSession={deletedThisSession}
                importPruned={importPruned}
            />
        </div>
    );
}
